import type { Track } from '@/world/Track';
import { tuning } from '@/config/tuning';
import { lerp } from '@/math/angles';
import { forwardX, forwardZ } from '@/math/orientation';
import { ballStep, createBallState, type BallParams, type BallPhysicsState } from '@/physics/ball';
import { resolvePlaneWall, resolveWall } from '@/physics/collisions';
import {
  catchInNet,
  crossbarHit,
  nearGoal,
  netDepthAt,
  netGripKeep,
  roofHold,
  toGoalCoords,
} from '@/physics/goalNet';

/** M117d: ball params for a shortcut CHUTE — the road's values with a smooth
 *  deck's lighter rolling drag. Memoized: allocating per step would churn. */
let chuteParams: BallParams | null = null;
function chuteBallParams(): BallParams {
  if (!chuteParams) {
    chuteParams = { ...tuning.ball, rollingResistance: tuning.shortcuts.chuteRolling };
  }
  return chuteParams;
}

/** What BallView needs to draw + roll the ball. */
export interface BallRenderSnapshot {
  x: number;
  y: number;
  z: number;
  vx: number;
  vz: number;
}

/**
 * Ball.ts — the ball ENTITY. Owns the pure physics state and layers the track
 * interaction (continuous ground height + lateral wall bounce via the shared
 * resolveWall helper) on top. Car/ball/ball collisions are applied by the game
 * after every entity has stepped.
 */
export class Ball {
  readonly state: BallPhysicsState;
  hint = 0;

  /** Render-only visual offset for online correction smoothing (0 offline). */
  rOffX = 0;
  rOffY = 0;
  rOffZ = 0;

  private prevX = 0;
  private prevY = 0;
  private prevZ = 0;

  constructor(x: number, y: number, z: number, radius = tuning.ball.radius) {
    this.state = createBallState(x, y, z, radius);
    this.syncPrev();
  }

  reset(x: number, y: number, z: number): void {
    this.state.x = x;
    this.state.y = y;
    this.state.z = z;
    this.state.vx = 0;
    this.state.vy = 0;
    this.state.vz = 0;
    this.hint = 0;
    this.rOffX = this.rOffY = this.rOffZ = 0;
    this.syncPrev();
  }

  private syncPrev(): void {
    this.prevX = this.state.x;
    this.prevY = this.state.y;
    this.prevZ = this.state.z;
  }

  /** See Car.settleRenderPose (M143): a pose written from outside the step
   *  loop must not be blended in from the ball's old position. */
  settleRenderPose(): void {
    this.rOffX = this.rOffY = this.rOffZ = 0;
    this.syncPrev();
  }

  get speed(): number {
    return Math.hypot(this.state.vx, this.state.vz);
  }

  update(dt: number, track: Track): void {
    this.syncPrev();

    // Ground height beneath the ball, then the pure step.
    const pre = track.project(this.state.x, this.state.z, this.hint);
    ballStep(this.state, pre.y, track.chuteRoll > 0 ? chuteBallParams() : tuning.ball, dt);

    // Re-project after the move for the new ground + walls.
    const proj = track.project(this.state.x, this.state.z, this.hint);
    this.hint = proj.index;

    const restY = proj.y + this.state.radius;
    if (this.state.y < restY) {
      this.state.y = restY;
      if (this.state.vy < 0) this.state.vy = 0;
    }

    // M117d SHORTCUT CHUTE (owner rule: "shortcuts mostly downhill so that if
    // a ball makes it onto the shortcut without the user it continues down
    // the path to the exit"). A grounded ball on a deck is pulled ALONG the
    // deck by its fall — measured from the real surface either side, so the
    // pull is exactly the geometry the player sees. Channel-only: the road's
    // chuteRoll is 0, so course ball physics never changes.
    if (track.chuteRoll > 0 && this.state.y <= restY + 0.06) {
      const fx = forwardX(proj.dir);
      const fz = forwardZ(proj.dir);
      const ds = 2;
      const ahead = track.heightAt(this.state.x + fx * ds, this.state.z + fz * ds, proj.index);
      const behind = track.heightAt(this.state.x - fx * ds, this.state.z - fz * ds, proj.index);
      // A chute is a ONE-WAY slide: where the deck is level or tips up (a
      // terrain-forced flat, or the in-road stub climbing at the exit) the
      // pull is simply absent — it must never shove a ball back up the deck,
      // which measurably stalled a rolling ball 8 u short of the hand-off.
      const fall = Math.max(0, (behind - ahead) / (2 * ds));
      const a = track.chuteRoll * fall * dt;
      this.state.vx += fx * a;
      this.state.vz += fz * a;
    }

    const w = resolveWall(
      proj.lateral,
      track.halfWidth - this.state.radius,
      proj.rightX,
      proj.rightZ,
      this.state.vx,
      this.state.vz,
      tuning.ball.wallRestitution,
    );
    if (w) {
      this.state.x += w.pushX;
      this.state.z += w.pushZ;
      this.state.vx = w.vx;
      this.state.vz = w.vz;
    }

    // Course end walls (M42): the back barrier keeps a knocked-back ball on the
    // course; the far one closes the finish run-out. Both are no-ops mid-race.
    // Shortcut channels opt out (M117c) — a ball can roll back off the deck.
    if (track.noEndWalls) return;
    for (const wall of [track.endWalls.back, track.endWalls.end]) {
      const ew = resolvePlaneWall(
        this.state.x,
        this.state.z,
        this.state.radius,
        wall.x,
        wall.z,
        wall.nx,
        wall.nz,
        this.state.vx,
        this.state.vz,
        tuning.ball.wallRestitution,
      );
      if (ew) {
        this.state.x += ew.pushX;
        this.state.z += ew.pushZ;
        this.state.vx = ew.vx;
        this.state.vz = ew.vz;
      }
    }
    this.applyGoal(track, dt);
  }

  /** THE FINISH GOAL (M157): the crossbar, the roof net and the back net. A
   *  ball arriving at race pace bulges the net, stops and drops back a short
   *  way, settling inside the goal. Shares physics/goalNet.ts with the car. */
  private applyGoal(track: Track, dt: number): void {
    const g = track.goalFrame;
    if (!g) return;
    const st = this.state;
    const c = toGoalCoords(g, st.x, st.z);
    if (!nearGoal(g, c, st.radius)) return;
    let s = c.s;
    let y = st.y;
    let vy = st.vy;
    let vs = st.vx * g.fx + st.vz * g.fz;
    let vl = st.vx * g.rx + st.vz * g.rz;
    const bar = crossbarHit(s, y - g.y, st.radius, vs, vy);
    if (bar) {
      s += bar.ds;
      y += bar.dh;
      vs = bar.vs;
      vy = bar.vh;
    }
    const roof = roofHold(s, y - g.y, st.radius, vy);
    if (roof) {
      y = g.y + roof.h;
      vy = roof.vh;
    }
    const net = catchInNet(s, st.radius, netDepthAt(y - g.y), vs, dt);
    if (net) {
      s = net.s;
      vs = net.vAlong;
      // the mesh grabs: sideways skating and any upward bounce are soaked up
      const keep = netGripKeep(dt);
      vl *= keep;
      if (vy > 0) vy *= keep;
    }
    if (!bar && !roof && !net) return;
    st.x += g.fx * (s - c.s);
    st.z += g.fz * (s - c.s);
    st.y = y;
    st.vy = vy;
    st.vx = g.fx * vs + g.rx * vl;
    st.vz = g.fz * vs + g.rz * vl;
  }

  snapshot(alpha: number): BallRenderSnapshot {
    return {
      x: lerp(this.prevX, this.state.x, alpha) + this.rOffX,
      y: lerp(this.prevY, this.state.y, alpha) + this.rOffY,
      z: lerp(this.prevZ, this.state.z, alpha) + this.rOffZ,
      vx: this.state.vx,
      vz: this.state.vz,
    };
  }
}
